<template>
  <div class="product-overview">
    <div class="po-figure grid">
      <fn-product-overview-summary
        class="po-summary-mobile"
        :average-rating="product.averageRating"
        :badges="productBadges"
        :biocide="product.biocide"
        :brand="product.brandName"
        :breadcrumb-link="breadcrumbLink"
        :breadcrumb-link-crawl-hidden="breadcrumbLinkCrawlHidden"
        :sizes-and-measurements="hasSizeTablesOrMeasureInfo"
        :materials-and-care-instructions="hasMaterialsOrCareInstructions"
        :composition="product.composition"
        :discount="product.pricing?.savingsRelative?.formattedValue"
        :feeding-suggestion="!!product.feedingSuggestions"
        :has-biocide-warning="hasBiocideWarning"
        :headline-type="isMobileView ? 'h1' : 'div'"
        :name="product.name"
        :nodes="nodes"
        :number-of-reviews="product.numberOfReviews"
        :payback-points="product.paybackPoints"
        :product-code="product.code"
        :selling-points="product.sellingPoints"
        :variant-name="product.variantName"
        @change-tab="(tab, insideTab) => $emit('changeTab', tab, insideTab)"
        @modal-sizes-and-measurements-opened="$emit('modalSizesAndMeasurementsOpened')"
      />

      <fn-gallery
        :key="mediaDetails.length"
        :media="mediaDetails"
        :product-id="product.code"
        class="po-overview-gallery grid-item grid-item--m--6"
        enable-image-seo
        first-image-has-high-prio
      >
        <template v-if="!isMobileView">
          <fn-headline
            size="h3"
            type="h2"
          >
            {{ product.brandName }} {{ product.name }} {{ product.variantName }}
          </fn-headline>

          <fn-rating
            :count="product.totalNumberOfReviews ? product.totalNumberOfReviews : null"
            :product-code="product.code"
            :stars="product.totalAvgRating ? product.totalAvgRating : 0"
            bv-enabled
            product-overview-page
            show-summary-on-hover
            tag="a"
          />
        </template>
      </fn-gallery>

      <div class="po-overview-and-actions grid-item--m--6 grid-item">
        <fn-product-overview-summary
          class="po-summary-desktop"
          :average-rating="product.averageRating"
          :badges="productBadges"
          :biocide="product.biocide"
          :brand="product.brandName"
          :breadcrumb-link="breadcrumbLink"
          :breadcrumb-link-crawl-hidden="breadcrumbLinkCrawlHidden"
          :composition="product.composition"
          :discount="product.pricing?.savingsRelative?.formattedValue"
          :feeding-suggestion="!!product.feedingSuggestions"
          :has-biocide-warning="hasBiocideWarning"
          :headline-type="isMobileView ? 'div' : 'h1'"
          :materials-and-care-instructions="hasMaterialsOrCareInstructions"
          :name="product.name"
          :nodes="nodes"
          :number-of-reviews="product.numberOfReviews"
          :payback-points="product.paybackPoints"
          :product-code="product.code"
          :selling-points="product.sellingPoints"
          :sizes-and-measurements="hasSizeTablesOrMeasureInfo"
          :variant-name="product.variantName"
          @change-tab="(tab, insideTab) => $emit('changeTab', tab, insideTab)"
          @modal-sizes-and-measurements-opened="$emit('modalSizesAndMeasurementsOpened')"
        />

        <fn-vuex-add-to-cart-form
          :product="product"
          :has-size-tables-or-measure-info="hasSizeTablesOrMeasureInfo"
          show-shipping-information
          @add-to-cart-overlay="$emit('addToCartOverlay', $event)"
          @change-tab="$emit('changeTab', $event)"
          @change-variant="$emit('changeVariant', $event)"
          @modal-sizes-and-measurements-opened="$emit('modalSizesAndMeasurementsOpened')"
        />

        <fn-product-availability :product="product" />

        <fn-vet-disclaimer
          v-if="showDisclaimer"
          id="vet-disclaimer-wrapper"
          :text="$t('product.disclaimer')"
          @set-active-tab="setActiveTab"
        />

        <fn-adobe-target-mbox-wrapper v-slot="{ visible }">
          <fn-section
            v-show="visible"
            constrained="l"
            no-padding-x
            padding-bottom="xs"
            padding-top="s"
          >
            <fn-adobe-target-mbox id="recoSlotSingle" />
          </fn-section>
        </fn-adobe-target-mbox-wrapper>
      </div>

      <div
        v-if="hasBiocideWarning"
        class="pos-warning pos-warning--biozide"
      >
        <div v-html="$t('product.warnings.contains_pesticides')" />
      </div>

      <fn-product-badges
        v-if="showProductBadges"
        class="po-badges"
        :badges="productBadges"
        :discount="product.pricing?.savingsRelative?.formattedValue"
        :payback-points="product.paybackPoints"
        column
        show-price-tip
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import ProductBadges from '@/mixins/ProductBadges';

export default defineComponent({
  mixins: [ProductBadges],
  props: {
    product: {
      type: Object,
      required: true,
    },
    nodes: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['changeTab', 'modalSizesAndMeasurementsOpened', 'addToCartOverlay', 'changeVariant'],
  data: () => ({
    breadcrumbLinkCrawlHidden: false,
    sovidoVideoData: null,
  }),
  computed: {
    ...mapState(useMainStore, {
      language: 'language',
      features: 'getFeature',
      sovidoEnabled: 'getSovidoEnabled',
      paybackEnabled: 'getPaybackEnabled',
      isMobileView: 'isMobileView',
    }),

    sovidoEnabledAndAvailable() {
      return this.sovidoEnabled && this.sovidoVideoData;
    },

    showDisclaimer() {
      return (
        this.features('VETNoticeEnabled') &&
        this.product.analytics.categories.some(
          (category) => category === 'foodVetDietsDog' || category === 'foodVetDietsCat',
        )
      );
    },

    mediaDetails() {
      const { videos = [], images = [] } = this.product;

      if (!videos.length && !images.length && !this.sovidoEnabledAndAvailable) {
        return null;
      }

      const videosArr = videos.map((videoData) => ({
        ...videoData,
        type: 'video',
      }));

      if (this.sovidoEnabledAndAvailable) {
        videosArr.unshift({
          type: 'sovido_video',
          coverImage: this.sovidoVideoData.playlist_cover_image_url,
        });
      }

      return [...images.slice(0, 1), ...videosArr, ...images.slice(1)];
    },

    hasBiocideWarning() {
      if (!this.product.warnings) {
        return false;
      }
      return !!this.product.warnings.filter((warning) => warning === 'contains_pesticides').length;
    },

    breadcrumbLink() {
      if (!this.nodes || this.nodes.length <= 0) {
        return null;
      }

      if (this.nodes.length <= 2) {
        return `${this.$t('url.search')}?q=${encodeURIComponent(`::brand:${this.product.brandCode}`)}`;
      }
      const categoryIndex = this.nodes.length - 2;
      const nodeUrl = this.nodes[categoryIndex].url;

      const url = nodeUrl
        ? `${nodeUrl}${nodeUrl.includes('?q') ? encodeURIComponent(`:brand:${this.product.brandCode}`) : `?q=${encodeURIComponent(`::brand:${this.product.brandCode}`)}`}`
        : this.nodes[categoryIndex].truncateQuery.url;
      return url;
    },

    hasSizeTablesOrMeasureInfo() {
      return (
        !!Object.keys(this.product?.measureInfo || {}).length ||
        !!this.product?.sizeTable?.table?.length ||
        !!this.product?.sizeTableMeasurement?.table?.length
      );
    },

    hasMaterialsOrCareInstructions() {
      return !!this.product?.cleaningRecommendations?.length || !!Object.keys(this.product?.material || {}).length;
    },

    showProductBadges() {
      return this.productBadges || this.product.paybackPoints;
    },
  },
  async created() {
    if (!this.breadcrumbLink) {
      return;
    }

    try {
      await this.$api.hybris.seocontent.find(this.breadcrumbLink, this.language);
    } catch (error) {
      this.breadcrumbLinkCrawlHidden = true;
    }
  },
  mounted() {
    if (this.sovidoEnabled) {
      document.addEventListener('sovidoPlayerLoaded', this.setSovidoData);
    }
  },
  beforeUnmount() {
    if (this.sovidoEnabled) {
      document.removeEventListener('sovidoPlayerLoaded', this.setSovidoData);
    }
  },
  methods: {
    setSovidoData(event) {
      if (this.sovidoVideoData) {
        return;
      }

      if (!event?.detail?.videos) {
        return;
      }

      [this.sovidoVideoData] = event.detail.videos;
    },
    setActiveTab(tab) {
      this.$emit('setActiveTab', tab);
    },
  },
});
</script>

<style lang="scss" scoped>
.product-overview {
  .po-figure {
    .po-overview-gallery {
      order: 1;
      z-index: 1;
      margin-bottom: $pad * 1.25;
    }

    :deep(.po-summary-mobile) {
      order: 1;

      @include bp(m) {
        display: none;
      }
    }

    :deep(.po-summary-desktop) {
      @include bp(m, max) {
        display: none;
      }
    }

    .po-overview-and-actions {
      order: 2;
    }

    .po-badges {
      margin-top: 24px;

      @include bp(m) {
        display: none;
      }
    }

    :deep(.g-footer) {
      margin-top: $pad * 1.65;
      display: flex;
      justify-content: center;
    }

    .pos-warning--biozide {
      margin-top: 1.5 * $pad;

      @include bp(m) {
        display: none;
      }
    }

    @include bp(m, max) {
      display: flex;
      flex-direction: column;

      .po-overview-gallery {
        order: 2;
      }

      .pos-warning {
        order: 3;
      }

      :deep(.product-badges) {
        order: 4;
      }

      .po-overview-and-actions {
        order: 5;
      }
    }
  }

  .grid {
    @include bp(m) {
      flex-direction: row;
      @include grid-row();
    }

    .grid-item {
      @include bp(m) {
        @include grid-item('grid-item');
      }
    }
  }

  .po-tax-info {
    margin: $pad $pad 0 0;
    text-align: right;
    font-size: map-get($font-sizes, 's');
    color: palette(content, secondary);
  }

  &:deep(.payback-badge) {
    @include bp(m, max) {
      padding: 0;
    }
  }

  :deep(.product-variant-slider) {
    .swiper-container {
      z-index: unset;
    }
  }
}
</style>
